import { AgChartThemeOverrides, ChartModel, ChartType, CreateRangeChartParams, GridApi, GridOptions } from "ag-grid-community";
import { AgGridChartRef, CreateChartParams } from "src/shared/models/ag-grid-chart.models";

export default abstract class AgGridChartBase {
    private baseRangeChartParams: CreateRangeChartParams = {
        cellRange: {
            columns: null,
            rowStartIndex: 0,
            rowEndIndex: null
        },
        chartType: 'groupedBar',
        suppressChartRanges: true,
        aggFunc: 'sum'
    }

    /**
     * Create and render ag-chart integrated with ag-grid based on the provided options.
     * @param api GridApi that the chart is base on
     */
    public createChart(api: GridApi, chartRef: AgGridChartRef, params: CreateChartParams): AgGridChartRef {
        if (!api) {
            console.error(`Failed to create chart. Grid api undefined.`);
            return;
        }

        if (chartRef?.chartRef) // If the chart already exists, remove existing chart to prevent duplicates
            chartRef.chartRef.destroyChart();
            
        return {
            chartRef: api.createRangeChart(angular.extend(angular.copy(this.baseRangeChartParams), params)),
            chartParams: params 
        }
    }

    public getChartState(api: GridApi): ChartModel[] {
        if (!api)
            return;

        // Get chart models and simplify the model for JSON
        return api.getChartModels().map((cm: ChartModel) => {
            return cm;
        });
    }
    
    public loadSingleChartState(api: GridApi, chartRef: AgGridChartRef, stateModel: ChartModel): AgGridChartRef {
        if (!chartRef) {
            console.error(`Failed to load chart state. chartRef is undefined.`);
            return;
        }
        
        if (!stateModel) {
            console.error(`Failed to load chart state. stateModel is undefined.`);
            return;
        }

        return this.loadChartState(api, [chartRef], [stateModel])[0];
    }
    
    public loadMultipleChartStates(api: GridApi, chartRefs: AgGridChartRef[], stateModels: ChartModel[]): AgGridChartRef[] {
        if (!chartRefs.length) {
            console.error(`Failed to load chart state. chartRefs is undefined or empty.`);
            return;
        }
        
        if (!stateModels?.length) {
            console.error(`Failed to load chart state. stateModels is undefined or empty.`);
            return;
        }
        
        if (chartRefs.length != stateModels.length) {
            console.error(`Failed to load chart state. There was a mismatch between the number of chartRefs and stateModels`);
            return;
        }
        
        return this.loadChartState(api, chartRefs, stateModels);
    }

    /**
     * 
     * @param options 
     * @param state 
     * @returns 
     */
    private loadChartState(api: GridApi, chartRefs: AgGridChartRef[], stateModels: ChartModel[]): AgGridChartRef[] {
        if (!api) {
            console.error(`Failed to load chart state. Grid api is undefined.`);
            return;
        }
        
        var models = api.getChartModels();
        var newChartRefs = new Array<AgGridChartRef>();
        
        for (var i = 0; i < stateModels.length; i++) {
            var chartParams: CreateChartParams = {
                cellRange: stateModels[i].cellRange,
                chartType: stateModels[i].chartType,
                chartThemeName: stateModels[i].chartThemeName,
                chartContainer: chartRefs[i].chartParams.chartContainer,
                suppressChartRanges: stateModels[i].suppressChartRanges,
                aggFunc: stateModels[i].aggFunc,
                // chartThemeOverrides: Should use instead of processChartOptions but has many bugs on current version of Ag-Grid
                unlinkChart: stateModels[i].unlinkChart,
                // processChartOptions: params => angular.extend(params.options, stateModels?.[i]?.chartOptions) // Deprecated and should be replaced in the future
            }
            
            models[i] = angular.extend(models[i], stateModels[i]);
            newChartRefs.push(this.createChart(api, chartRefs[i], chartParams));
        }
        
        return newChartRefs;
    }
}
